import { useEffect, useRef, type ReactNode } from 'react';
import { Icons } from '../Icons';

// Stack-aware focus trap (port z FarmaPro): tylko najgłębszy modal w stosie
// reaguje na Tab/Escape. Zapobiega konfliktom przy nested modal flow.
const modalStack: HTMLDivElement[] = [];
const isTopModal = (container: HTMLDivElement | null): boolean =>
  container !== null && modalStack[modalStack.length - 1] === container;

export type ModalStatus = 'neutral' | 'danger' | 'success' | 'warn' | 'info' | 'savings';
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  onClose: () => void;
  title?: string;
  subtitle?: string;
  icon?: ReactNode;
  status?: ModalStatus;
  size?: ModalSize;
  ariaLabel?: string;
  closeDisabled?: boolean;
  hideCloseButton?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

const statusRingClass: Record<ModalStatus, string> = {
  neutral: '',
  danger: 'ring-1 ring-rose-500/30 dark:ring-rose-400/40',
  success: 'ring-1 ring-emerald-500/30 dark:ring-emerald-400/40',
  warn: 'ring-1 ring-amber-500/40 dark:ring-amber-400/50',
  info: 'ring-1 ring-sky-500/40 dark:ring-sky-400/50',
  savings: 'ring-1 ring-violet-500/40 dark:ring-violet-400/50',
};

export function Modal({
  onClose,
  title,
  subtitle,
  icon,
  status = 'neutral',
  size = 'sm',
  ariaLabel,
  closeDisabled,
  hideCloseButton,
  children,
  footer,
}: ModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    if (container) modalStack.push(container);

    const t = window.setTimeout(() => {
      const c = containerRef.current;
      if (!c) return;
      const focusables = c.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const skipClose = Array.from(focusables).filter((el) => el.getAttribute('aria-label') !== 'Zamknij');
      const target = skipClose[0] ?? focusables[0];
      target?.focus();
    }, 30);

    const handler = (e: KeyboardEvent) => {
      if (!isTopModal(containerRef.current)) return;
      if (e.key === 'Escape' && !closeDisabled) {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const c = containerRef.current;
      if (!c) return;
      const focusables = c.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';

    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
      if (container) {
        const idx = modalStack.indexOf(container);
        if (idx >= 0) modalStack.splice(idx, 1);
      }
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        try {
          prev.focus();
        } catch {
          /* no-op */
        }
      }
    };
  }, [onClose, closeDisabled]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200 backdrop-blur-md bg-slate-900/50">
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        className={`relative w-full ${sizeClass[size]} bg-white dark:bg-slate-900/95 rounded-modal ${statusRingClass[status]} shadow-2xl border border-slate-100 dark:border-slate-800 max-h-[90vh] overflow-y-auto overflow-x-hidden font-sans animate-in zoom-in-95 duration-200`}
      >
        {!hideCloseButton && (
          <button
            onClick={onClose}
            aria-label="Zamknij"
            disabled={closeDisabled}
            className="absolute top-4 right-4 z-10 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors disabled:opacity-50"
          >
            <Icons.X size={20} />
          </button>
        )}

        {(title || icon) && (
          <div className="flex flex-col items-center pt-8 pb-2 px-4 sm:px-8">
            {icon && <div className="mb-3">{icon}</div>}
            {title && <h2 className="text-xl sm:text-2xl font-display font-bold text-slate-900 dark:text-white text-center tracking-tight">{title}</h2>}
            {subtitle && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 text-center">{subtitle}</p>}
          </div>
        )}

        <div className="px-4 sm:px-8 py-4">{children}</div>

        {footer && (
          <div className="px-4 sm:px-8 pb-6 sm:pb-8 pt-2 border-t border-slate-100 dark:border-slate-800 mt-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default Modal;
